import { FC } from "react";

import { Box, Stack } from "@mui/material";

import { useTranslate } from "@/common/hooks/useTranslate";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { FormTextButton } from "@/forms-design-system/FormTextButton";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { Check, ExternalLink, Lock } from "@/icons/outlined";

interface SupportedProviderStepProps {
  providerName: string;
  amount: string;
  isConfirmation: boolean;
  isEmbed?: boolean;
  isBusy: boolean;
  actionError: boolean;
  onOpenProvider: () => void;
  onConfirm: () => void;
  onBack: () => void;
}

export const SupportedProviderStep: FC<SupportedProviderStepProps> = ({
  providerName,
  amount,
  isConfirmation,
  isEmbed = false,
  isBusy,
  actionError,
  onOpenProvider,
  onConfirm,
  onBack,
}) => {
  const { t } = useTranslate();
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      gap={5}
      padding={2}
      sx={(theme) => ({
        minHeight: isEmbed ? "auto" : "100dvh",
        width: "100%",
        maxWidth: theme.spacing(64.5),
        marginInline: "auto",
      })}
      data-test="daf-supported-provider-step"
    >
      <Box
        component="img"
        alt=""
        src={isConfirmation ? "/assets/daf-provider-success.svg" : "/assets/daf-provider-help.svg"}
        sx={(theme) => ({
          width: theme.spacing(isConfirmation ? 26.375 : 22.375),
          height: theme.spacing(isConfirmation ? 22.875 : 26.625),
        })}
      />
      <Stack gap={isConfirmation ? 4 : 3} alignItems="center" width="100%">
        <Stack gap={1.5} width="100%" alignItems="center">
          <FormTypography variant="h5" vibe="brand" textAlign="center" width="100%">
            {t("common", isConfirmation ? "daf.confirmationTitle" : "daf.requestTitle", {
              provider: providerName,
              amount,
            })}
          </FormTypography>
          {isConfirmation && (
            <FormTextButton onClick={onOpenProvider} sx={(theme) => ({ ...theme.typography.body1 })}>
              <Box component="span" sx={{ textDecoration: "underline" }}>
                {t("common", "daf.openPortal", { provider: providerName })}
              </Box>
              <FormIcon size="medium" sx={{ ml: 0.5 }}>
                <ExternalLink />
              </FormIcon>
            </FormTextButton>
          )}
        </Stack>
        {actionError && (
          <FormTypography role="alert" vibe="error">
            {t("common", "daf.updateError")}
          </FormTypography>
        )}
        <Stack gap={2} alignItems="center" maxWidth="100%">
          <FormButton
            size="large"
            onClick={isConfirmation ? onConfirm : onOpenProvider}
            isLoading={isBusy}
            endIcon={isConfirmation ? <Check /> : <ExternalLink />}
            components={{ endIcon: { sx: { marginInlineStart: 0.5 } } }}
            sx={(theme) => ({
              ...theme.typography.subtitle1,
              boxShadow: theme.boxShadow.base.strong,
              maxWidth: "100%",
              height: "auto",
              minHeight: theme.spacing(5.5),
              whiteSpace: "normal",
            })}
            data-test={isConfirmation ? "daf-confirm" : "daf-continue-provider"}
          >
            {t("common", isConfirmation ? "daf.confirmSupported" : "daf.continueProvider", { provider: providerName })}
          </FormButton>
          {isConfirmation ? (
            <FormButton
              variant="outline"
              onClick={onBack}
              disabled={isBusy}
              sx={{ maxWidth: "100%", whiteSpace: "normal" }}
              data-test="daf-back"
            >
              {t("common", "daf.backSupported")}
            </FormButton>
          ) : (
            <Stack direction="row" gap={1} alignItems="center">
              <FormIcon size="small" sx={(theme) => ({ color: theme.palette.forms.text.quiet })}>
                <Lock />
              </FormIcon>
              <FormTypography variant="subtitle2" vibe="quiet">
                {t("common", "daf.secured")}
              </FormTypography>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
